/* eslint-disable no-use-before-define  */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import animator from '../../../helpers/animator';
import CompanyNavCard from './CompanyNavCard';
import CompanyAddCard from './CompanyAddCard';
import AddIcon from '../../../assets/images/add-icon-white.svg';
import dataModel from '../../../models/DataModel';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import LoadingDots from '../../../components/LoadingDots';
import './CompaniesNav.scss';
import ProjectSearchFilter from '../../../components/ProjectsNav/ProjectSearchFilter';
import _ from 'lodash';

const CompaniesNav = ({
  dataModel,
  permissions,
  currentUser,
  currentCompany,
  locations,
  selectCompany,
  editCompany,
  handleCompanyDelete,
  selectedCompany,
  readOnly,
}) => {
  const [companies, setCompanies] = useState([]);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);
  const [displayAddCard, setDisplayAddCard] = useState(false);
  const [searchResults, setSearchResults] = useState('');
  const [filter] = useState(null);
  const [resetSearchString] = useState(false);
  const [projectName] = useState('');
  const [, setRefresh] = useState(false);
  const companiesCollectionDataSource = useRef(null);
  const scroller = useRef(null);
  const companyMap = useRef({});
  const companiesRef = useRef([]);
  const initializing = useRef(true);
  const scrollToAddCard = useRef(false);
  const textMap = {
    companies: 'Companies',
    searchCompany: 'Search Company',
  };
  const animatorInstance = useRef(new animator());

  // Debounced search
  const debouncedProjectTypeSearch = useCallback(
    _.debounce((searchString) => {
      setSearchResults(searchString);
      handleCompanySearch(searchString);
    }, 400),
    []
  );

  // DataSource update handler
  const handleCompaniesUpdate = useCallback((companiesData, action, acData, fetchedData) => {
    if (action === 'prefetch') {
      setLoadingProjectTypes(true);
      setCompanies(companiesData.companies);
    } else {
      setLoadingProjectTypes(false);
      setCompanies(companiesData.companies);
    }

    if (acData) {
      if (acData.type === 'newCompany' && acData.updating_user_id === currentUser.id) {
        if (selectCompany) selectCompany(fetchedData);
      } else if (acData.type === 'deleteCompany') {
        companiesCollectionDataSource.current.current();
      }
      if (action !== 'prefetch' && !acData) {
        if (selectedCompany) {
          const element = document.getElementById('company-' + selectedCompany.id);
          if (element) {
            scrollIntoView(element, 'company-' + selectedCompany.id);
          }
        }
      }
    }
  }, [currentUser, selectCompany, selectedCompany]);

  // DataSource setup
  useEffect(() => {
    if (currentCompany && currentUser) {
      companiesCollectionDataSource.current = new CollectionDataSource(
        'CompaniesCollection',
        'Company',
        { companies: { vendor_companies: { additionalFields: ['is_vendor_company'] } } },
        handleCompaniesUpdate,
        'headerCompanies',
        null,
        null,
        null,
        currentCompany.id,
        'companies_nav'
      );
      companiesCollectionDataSource.current.current();
    }
    return () => {
      companiesCollectionDataSource.current.release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, currentUser]);

  // Scroll into view logic
  const scrollIntoView = (element) => {
    if (!scroller.current) return;
    var rect = element.getBoundingClientRect();
    var parentElement = element.parentElement;
    var parentRect = parentElement.getBoundingClientRect();
    var scrollRect = scroller.current.getBoundingClientRect();
    const id = scroller.current.id;
    if (rect.top <= scrollRect.top) {
      let amountAlreadyScrolled = Math.abs(scrollRect.top - parentRect.top);
      let additionalScroll = scrollRect.top - rect.top;
      let scrollAmount = amountAlreadyScrolled - additionalScroll;
      const ani = animatorInstance.current.createPropertyAnimation(id, 250, { verticalScroll: { startVal: amountAlreadyScrolled, endVal: scrollAmount } });
      animatorInstance.current.addAnimation(ani);
      animatorInstance.current.processAnimationQueue();
    } else if (rect.bottom >= scrollRect.bottom) {
      let amountAlreadyScrolled = Math.abs(scrollRect.top - parentRect.top);
      let additionalScroll = rect.bottom - scrollRect.bottom;
      let scrollAmount = amountAlreadyScrolled + additionalScroll;
      const ani = animatorInstance.current.createPropertyAnimation(id, 250, { verticalScroll: { startVal: amountAlreadyScrolled, endVal: scrollAmount } });
      animatorInstance.current.addAnimation(ani);
      animatorInstance.current.processAnimationQueue();
    }
  };

  // Scroll to selected company/add card on update
  useEffect(() => {
    if (selectedCompany) {
      const element = document.getElementById('company-' + selectedCompany.id);
      if (element) scrollIntoView(element);
    }
    if (scrollToAddCard.current) {
      scrollToAddCard.current = false;
      const element = document.getElementById('company-add-card');
      if (element) scrollIntoView(element);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  // Company selection handlers
  const handleSelectCompany = (company, parentCompanyId) => {
    if (selectCompany) selectCompany(company, parentCompanyId);
  };
  const handleEditCompany = (company) => {
    if (editCompany) editCompany(company);
  };

  const handleExpand = (company) => {
    if (companyMap.current && companyMap.current[company.id]) {
      companyMap.current[company.id].expanded = !companyMap.current[company.id].expanded;
      setRefresh(r => !r);
    }
  };

  // Company cards creation
  const createCompanyCards = () => {
    if (!companies || !currentUser || !permissions) return null;

    let newMap = false;
    if (!companyMap.current || !companiesRef.current || companiesRef.current.length !== companies.length) {
      companyMap.current = {};
      companiesRef.current = companies;
      newMap = true;
    }

    let filterCompanies = [];
    if (!companies || companies.error) return null;

    if (!permissions.isSuperUser) {
      filterCompanies = companies.filter(company => company.id === currentUser.primary_company_id);
    } else {
      filterCompanies = companies;
    }

    filterCompanies.sort((company1, company2) => {
      let company1Name = company1.name.toUpperCase();
      let company2Name = company2.name.toUpperCase();
      if (company1Name < company2Name) return -1;
      if (company1Name > company2Name) return 1;
      return 0;
    });

    if (searchResults || filter) {
      filterCompanies = handleFilterCompanies(filterCompanies);
    }

    let companyCards = [];
    for (let i = 0; i < filterCompanies.length; i++) {
      const company = filterCompanies[i];
      let selected = selectedCompany && !selectedCompany.is_vendor_company ? selectedCompany.id === company.id : false;

      if (newMap) {
        companyMap.current[company.id] = { expanded: false };
      }

      const companyCard = (
        <CompanyNavCard
          id={'company-' + company.id}
          key={'company-' + company.id}
          company={company}
          selectCompany={handleSelectCompany}
          hasVendors={currentUser.super_admin && company.vendor_companies && company.vendor_companies.length > 0}
          editCompany={handleEditCompany}
          selected={selected}
          handleDelete={handleCompanyDelete}
          expanded={
            companyMap.current &&
            Object.keys(companyMap.current).length > 0 &&
            companyMap.current[company.id] &&
            companyMap.current[company.id].expanded
          }
          handleExpand={handleExpand}
        />
      );
      companyCards.push(companyCard);

      if (
        currentUser.super_admin &&
        companyMap.current &&
        Object.keys(companyMap.current).length > 0 &&
        companyMap.current[company.id] &&
        companyMap.current[company.id].expanded &&
        company.vendor_companies &&
        company.vendor_companies.length > 0
      ) {
        for (let j = 0; j < company.vendor_companies.length; j++) {
          const vendorCompany = company.vendor_companies[j];
          selected = selectedCompany && selectedCompany.is_vendor_company && selectedCompany.id === vendorCompany.id;
          const vendorCard = (
            <CompanyNavCard
              id={'vendor-company-' + vendorCompany.id}
              key={'vendor-company-' + vendorCompany.id}
              company={vendorCompany}
              selectCompany={handleSelectCompany}
              editCompany={handleEditCompany}
              selected={selected}
              handleDelete={handleCompanyDelete}
              readOnly={readOnly}
            />
          );
          companyCards.push(vendorCard);
        }
      }
    }
    return companyCards;
  };

  // Filter companies by search/filter
  const handleFilterCompanies = (filterCompanies) => {
    let companiesArray = [];
    if (filter) {
      filterCompanies = filterCompanies.filter(company => {
        if (filter.project_template && filter.state) {
          return (
            company.project_template &&
            company.project_template.id === filter.project_template.id &&
            company.location &&
            company.location.state === filter.state
          );
        } else if (filter.project_template) {
          return company.project_template && company.project_template.id === filter.project_template.id;
        } else if (filter.state) {
          return company.location && company.location.state === filter.state;
        } else return false;
      });
    }
    if (filterCompanies.length > 0) {
      for (let i = 0; i < filterCompanies.length; i++) {
        if (filterCompanies[i].name.toLowerCase().includes(searchResults ? searchResults.toLowerCase() : '')) {
          companiesArray.push(filterCompanies[i]);
        }
      }
    }
    return companiesArray;
  };

  // Search handler
  const handleCompanySearch = (searchString) => {
    if (companiesCollectionDataSource.current) {
      companiesCollectionDataSource.current.searchString = searchString;
      companiesCollectionDataSource.current.current();
    }
  };
  const handleCompanySearchChange = (searchString) => {
    debouncedProjectTypeSearch(searchString);
  };

  // Add card handlers
  const handleDisplayAddCard = () => setDisplayAddCard(true);
  const handleCloseAddCard = () => setDisplayAddCard(false);

  const companyNavCards = createCompanyCards();
  initializing.current = false;
  let companyAddCard = null;
  if (displayAddCard) {
    companyAddCard = <CompanyAddCard id="company-add-card" closeAddCard={handleCloseAddCard} />;
    scrollToAddCard.current = true;
  }
  let addButton = null;
  if (permissions && permissions.isSuperUser && !displayAddCard) {
    addButton = (
      <img
        className="add-button"
        src={AddIcon}
        alt="Add"
        onClick={handleDisplayAddCard}
      />
    );
  }
  const numOfCompanies = companies.length;

  return (
    <div className="companies-nav">
      <div className="companies">
        <div className="section-header">
          {textMap.companies.toUpperCase()}
          {addButton}
        </div>
        <ProjectSearchFilter
          handleSearchChange={handleCompanySearchChange}
          search={projectName}
          resetSearchString={resetSearchString}
          overridePlaceHolder={textMap.searchCompany}
          hideFilter={true}
        />
        <div className='loading-dots-container'>
        {loadingProjectTypes && <div className="loading-dots"><LoadingDots /></div>}
        </div>
        <div id="company-scroller" className="companies-scroller" ref={scroller}>
          <div className="companies-list">
            {companyAddCard}
            {companyNavCards}
            {!loadingProjectTypes && numOfCompanies === 0 && (
              <div className='no-matches-container'>
                <div className="no-matches-indicator">No Results Found...</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({
  dataModel: dataModel,
  permissions: state.new_users ? state.new_users.permissions : null,
  currentUser: state.new_users ? state.new_users.currentUser : null,
  currentCompany: state.new_companies ? state.new_companies.currentCompany : null,
  locations: state.locationt ? state.locations.locations : null,
});

export default connect(mapStateToProps)(CompaniesNav);



// WEBPACK FOOTER //
// ./src/containers/CompaniesContainer/CompaniesNav/index.js